// Tab-side client for the per-origin query-permit SharedWorker.
//
// The SharedWorker (worker.ts) holds a global semaphore that caps the
// number of in-flight /api/query/* requests across all tabs in this origin. Tabs call
// acquireQueryPermit() before fetching and invoke the returned release thunk in a finally.
//
// Falls back to a no-op (resolves immediately, never throttles) when SharedWorker is
// unavailable (Chrome Android, some embedded webviews) or when the QuerySharedWorkerSemaphore
// FeatureFlag is disabled.

import {QUERY_PERMIT_HEARTBEAT_MS, TabToWorkerMessage, WorkerToTabMessage} from './protocol';

interface PendingAcquire {
  resolve: (release: () => void) => void;
}

interface PermitClient {
  acquire(reqId: string): Promise<() => void>;
}

let instantiatedClient: PermitClient | null | undefined = undefined;

function createSharedWorkerClient(port: MessagePort): PermitClient {
  const pending: Map<string, PendingAcquire> = new Map();
  const held: Set<string> = new Set();
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  function send(msg: TabToWorkerMessage): void {
    port.postMessage(msg);
  }

  function ensureHeartbeat(): void {
    if (heartbeatTimer !== null) {
      return;
    }
    heartbeatTimer = setInterval(() => send({type: `heartbeat`}), QUERY_PERMIT_HEARTBEAT_MS);
  }

  function maybeStopHeartbeat(): void {
    if (held.size === 0 && pending.size === 0 && heartbeatTimer !== null) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function release(reqId: string): void {
    if (!held.has(reqId)) {
      return;
    }
    held.delete(reqId);
    send({type: `release`, reqId});
    maybeStopHeartbeat();
  }

  port.addEventListener(`message`, (ev: MessageEvent<WorkerToTabMessage>) => {
    const msg = ev.data;
    if (msg.type === `granted`) {
      const waiter = pending.get(msg.reqId);
      if (!waiter) {
        // Granted after we gave up (e.g., reclaim race). Return the permit immediately.
        send({type: `release`, reqId: msg.reqId});
        return;
      }
      pending.delete(msg.reqId);
      held.add(msg.reqId);
      waiter.resolve(() => release(msg.reqId));
    } else if (msg.type === `reclaimed`) {
      // Worker took the permit back due to timeout. Drop from held so the eventual
      // release() call from the caller's finally block becomes a no-op.
      held.delete(msg.reqId);
      maybeStopHeartbeat();
    }
  });
  port.start();

  if (typeof window !== `undefined`) {
    window.addEventListener(`beforeunload`, () => {
      for (const reqId of Array.from(held)) {
        send({type: `release`, reqId});
      }
    });
  }

  return {
    acquire(reqId: string): Promise<() => void> {
      ensureHeartbeat();
      return new Promise<() => void>((resolve) => {
        pending.set(reqId, {resolve});
        send({type: `acquire`, reqId});
      });
    },
  };
}

const permitClientFactory: () => PermitClient | null = () => {
  if (typeof SharedWorker !== `undefined`) {
    try {
      // Vite's worker plugin requires literal option values; do not refactor into constants.
      const worker = new SharedWorker(new URL(`./worker.ts`, import.meta.url), {
        type: `module`,
        name: `mp-query-permits`,
      });
      return createSharedWorkerClient(worker.port);
    } catch {
      /** fall-through to local client */
    }
  }
  // TODO @alixmix: implement local client
  return null;
};

export function acquireQueryPermit(reqId: string): Promise<() => void> {
  if (instantiatedClient === undefined) {
    instantiatedClient = permitClientFactory();
  }
  if (instantiatedClient === null) {
    // TODO @alixmix: implement local fallback client.
    // GOOT: Return a no op promise.
    return Promise.resolve(() => {});
  }
  return instantiatedClient.acquire(reqId);
}
